import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ParentApi } from '../../services/api/ParentApi';
import './ParentInviteAccept.css';

/**
 * Child-facing page for responding to a parent (guardian) invite. Reached from
 * the invite email link (/parent-invite/accept?token=...). Requires the child
 * to be authenticated (enforced by ProtectedRoute); the backend additionally
 * verifies the logged-in email matches the invited email.
 */
function ParentInviteAccept() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [status, setStatus] = useState('loading'); // loading | valid | invalid | accepted | declined | error
  const [parentName, setParentName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) { setStatus('invalid'); return; }
      try {
        const resp = await ParentApi.validateInvite(token);
        const data = await resp.json().catch(() => ({}));
        if (!active) return;
        if (resp.ok && data.valid) {
          setParentName(data.parentName || 'A parent');
          setStatus('valid');
        } else {
          setStatus('invalid');
        }
      } catch (e) {
        if (active) setStatus('error');
      }
    })();
    return () => { active = false; };
  }, [token]);

  const handleAccept = useCallback(async () => {
    setSubmitting(true);
    setError('');
    try {
      const resp = await ParentApi.acceptInvite(token);
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setStatus('accepted');
      } else {
        setError(data.error || 'Could not accept the invite.');
      }
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [token]);

  const handleDecline = useCallback(async () => {
    setSubmitting(true);
    setError('');
    try {
      const resp = await ParentApi.declineInvite(token);
      if (resp.ok) {
        setStatus('declined');
      } else {
        const data = await resp.json().catch(() => ({}));
        setError(data.error || 'Could not decline the invite.');
      }
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [token]);

  return (
    <div className="parent-invite-accept">
      <div className="parent-invite-accept__card">
        {status === 'loading' && <p>Checking your invite…</p>}

        {status === 'invalid' && (
          <>
            <h2>Invite not found</h2>
            <p>This invite link is invalid, expired, or has already been used.</p>
            <button className="parent-invite-accept__btn" onClick={() => navigate('/dashboard')}>
              Go to my dashboard
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <h2>Something went wrong</h2>
            <p>We couldn't load this invite. Please try again later.</p>
          </>
        )}

        {status === 'valid' && (
          <>
            <h2>Parent connection request</h2>
            <p><strong>{parentName}</strong> would like to follow your learning progress on DocuHounds.</p>
            <p className="parent-invite-accept__note">
              If you accept, they'll be able to see your courses, assignments, and study activity.
            </p>
            {error && <p className="parent-invite-accept__error">{error}</p>}
            <div className="parent-invite-accept__actions">
              <button
                className="parent-invite-accept__btn parent-invite-accept__btn--primary"
                onClick={handleAccept}
                disabled={submitting}
              >
                {submitting ? 'Please wait…' : 'Accept'}
              </button>
              <button
                className="parent-invite-accept__btn parent-invite-accept__btn--ghost"
                onClick={handleDecline}
                disabled={submitting}
              >
                Decline
              </button>
            </div>
          </>
        )}

        {status === 'accepted' && (
          <>
            <h2>You're connected</h2>
            <p><strong>{parentName}</strong> can now follow your learning progress.</p>
            <button className="parent-invite-accept__btn" onClick={() => navigate('/dashboard')}>
              Continue to my dashboard
            </button>
          </>
        )}

        {status === 'declined' && (
          <>
            <h2>Invite declined</h2>
            <p>No connection was made. You can close this page.</p>
            <button className="parent-invite-accept__btn" onClick={() => navigate('/dashboard')}>
              Go to my dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default ParentInviteAccept;
